import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Divider,
  Box,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import {
  buscarTelefonoPorDni,
  buscarTelefonosReniec,
} from "../services/seeker.service";

/* ===================== TYPES ===================== */

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Resultado {
  numero: string;
  operador?: string;
  periodo?: string;
}

interface TelefonoUnificado {
  telefono: string;
  operador?: string;
  periodo?: string;
  fuente: "interna" | "externa" | "primary";
}

/* ===================== COMPONENT ===================== */

const DoxeoModal = ({ open, onClose }: Props) => {
  const [dni, setDni] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [telefonos, setTelefonos] = useState<TelefonoUnificado[]>([]);

  const handleBuscar = async () => {
  if (!dni) {
    setError("El DNI es obligatorio");
    return;
  }

  setLoading(true);
  setError(null);
  setTelefonos([]);

  try {

    const [respInterna, respExterna] = await Promise.all([
      buscarTelefonoPorDni(dni),
      buscarTelefonosReniec(dni)
    ]);

    /* ===================== INTERNA ===================== */

    let dataInterna = respInterna?.data?.data || [];

    if (!Array.isArray(dataInterna)) {
      dataInterna = dataInterna ? [dataInterna] : [];
    }

    const listaInterna: TelefonoUnificado[] = dataInterna
      .filter((r: any) => r.telefono && r.telefono !== "0")
      .map((r: any) => ({
        telefono: r.telefono,
        operador: r.operador,
        periodo: r.periodo,
        fuente: "interna",
      }));

    /* ===================== EXTERNA ===================== */

    const dataExterna = respExterna?.data;

    const listaExterna: TelefonoUnificado[] =
      dataExterna?.status === "ok" && Array.isArray(dataExterna.telefonos)
        ? dataExterna.telefonos
            .filter((t: any) => t.telefono && t.telefono !== "0")
            .map((t: any) => ({
              telefono: t.telefono,
              fuente: "externa",
            }))
        : [];

    /* ===================== UNION ===================== */

    const ordenFinal = [
      ...listaInterna,
      ...listaExterna
    ];

    if (ordenFinal.length > 0) {
      setTelefonos(ordenFinal);
    } else {
      setError("No se encontraron teléfonos asociados");
    }

  } catch (err) {
    console.error(err);
    setError("Error consultando el servicio");
  } finally {
    setLoading(false);
  }
};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Consulta por DNI
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <TextField
            label="DNI"
            value={dni}
            onChange={(e) => setDni(e.target.value)}
          />

          <Button variant="contained" onClick={handleBuscar} disabled={loading}>
            {loading ? <CircularProgress size={22} /> : "Buscar"}
          </Button>

          {error && <Typography color="error">{error}</Typography>}

          {telefonos.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />

              <Typography variant="h6">
                Teléfonos encontrados
              </Typography>

              <Stack spacing={2}>
                {telefonos.map((t, i) => (
                  <Box key={i} p={2} border="1px solid #ccc" borderRadius={2}>
                    <Typography>
                      {t.fuente === "interna" && "📡 OSIPTEL"}
                      {t.fuente === "externa" && "🌐 BD Externa"}
                      {t.fuente === "primary" && "⭐ Principal"} :{" "}
                      <b>{t.telefono}</b>
                    </Typography>

                    {t.operador && (
                      <Typography>
                        <b>Operador:</b> {t.operador}
                      </Typography>
                    )}

                    {t.periodo && (
                      <Typography>
                        <b>Periodo:</b> {t.periodo}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default DoxeoModal;